import { useRAF } from '@superbet-group/web.lib.vue-utilities';
import { defineComponent, ref, watchEffect, computed } from '@vue/composition-api';
import __vue_component__ from '../SdsTransitionGroup/SdsTransitionGroup.vue.js';

var changeFocusLabels = {
    start: 'data-sds-base-carousel-indicator-change-focus-labels-start',
    end: 'data-sds-base-carousel-indicator-change-focus-labels-end',
    previous: 'data-sds-base-carousel-indicator-change-focus-labels-previous',
    next: 'data-sds-base-carousel-indicator-change-focus-labels-next',
};
var staticWidth = 12;
var staticMaxTotal = 7;
var script = defineComponent({
    name: 'SdsBaseCarouselIndicator',
    props: {
        title: {
            type: String,
            required: true,
        },
        activeSlide: {
            type: Number,
            required: true,
        },
        slideTotal: {
            type: Number,
            required: true,
        },
        indicatorVariant: {
            type: String,
            required: true,
        },
    },
    emits: [
        'click',
    ],
    setup: function (props) {
        var baseCarouselIndicatorRef = ref(null);
        var focusElementRAF = useRAF(function (element) {
            element.focus();
        });
        function focusElement(origin, selector) {
            var element = origin.querySelector(selector);
            if (element instanceof HTMLElement) {
                focusElementRAF(element);
            }
        }
        function changeFocus(event) {
            if (!baseCarouselIndicatorRef.value || props.indicatorVariant !== 'interactive') {
                return;
            }
            switch (event.code) {
                case 'Home':
                    focusElement(baseCarouselIndicatorRef.value, "[".concat(changeFocusLabels.start, "]"));
                    event.preventDefault();
                    break;
                case 'End':
                    focusElement(baseCarouselIndicatorRef.value, "[".concat(changeFocusLabels.end, "]"));
                    event.preventDefault();
                    break;
                case 'ArrowLeft':
                    focusElement(baseCarouselIndicatorRef.value, "[".concat(changeFocusLabels.previous, "]"));
                    break;
                case 'ArrowRight':
                    focusElement(baseCarouselIndicatorRef.value, "[".concat(changeFocusLabels.next, "]"));
                    break;
                case 'ArrowUp':
                case 'ArrowDown':
                case 'PageUp':
                case 'PageDown':
                    event.preventDefault();
                    break;
            }
        }
        var focusActiveIndex = ref(null);
        function setFocusActiveIndex(index) {
            focusActiveIndex.value = index;
        }
        function getChangeFocusLabels(index) {
            var _a;
            if (focusActiveIndex.value === null) {
                return {};
            }
            return _a = {},
                _a[changeFocusLabels.start] = index === 0 ? '' : null,
                _a[changeFocusLabels.end] = index === props.slideTotal - 1 ? '' : null,
                _a[changeFocusLabels.previous] = index === focusActiveIndex.value - 1 ? '' : null,
                _a[changeFocusLabels.next] = index === focusActiveIndex.value + 1 ? '' : null,
                _a;
        }
        // This segment calculates the indices for the previous and next edge indicators based on the
        // current active slide, total number of slides and a maximum number of displayed indicators.
        var transientEdgeIndices = ref({
            previous: -1,
            next: props.slideTotal,
        });
        watchEffect(function () {
            // If the indicator variant is not 'static' return indicies that are out of bounds
            if (props.indicatorVariant !== 'static') {
                Object.assign(transientEdgeIndices.value, {
                    previous: -1,
                    next: props.slideTotal,
                });
            }
            // Calclulate general limits using active slide as a central point and a maximum number of displayed indicators.
            var previousLimit = props.activeSlide - Math.floor(staticMaxTotal / 2);
            var nextLimit = props.activeSlide + Math.floor(staticMaxTotal / 2);
            // Check has the calculated limit reached the edge and adjust them accordingly to not label the edges as transient.
            if (previousLimit <= 0) {
                Object.assign(transientEdgeIndices.value, {
                    previous: -1,
                    next: props.slideTotal > staticMaxTotal ? staticMaxTotal - 1 : props.slideTotal,
                });
                return;
            }
            if (nextLimit >= props.slideTotal - 1) {
                Object.assign(transientEdgeIndices.value, {
                    previous: props.slideTotal > staticMaxTotal ? props.slideTotal - staticMaxTotal : -1,
                    next: props.slideTotal,
                });
                return;
            }
            Object.assign(transientEdgeIndices.value, {
                previous: previousLimit,
                next: nextLimit,
            });
        });
        var CSSVariables = computed(function () { return ({
            '--sds-base-carousel-indicator-static-max-width': props.indicatorVariant === 'static'
                ? "".concat(staticMaxTotal * staticWidth, "px") : 'none',
            '--sds-base-carousel-indicator-static-scroll-offset': transientEdgeIndices.value.previous >= 0
                ? "".concat(-transientEdgeIndices.value.previous * staticWidth, "px") : '0px',
            '--sds-base-carousel-indicator-static-width': "".concat(staticWidth, "px"),
        }); });
        return {
            baseCarouselIndicatorRef: baseCarouselIndicatorRef,
            changeFocus: changeFocus,
            setFocusActiveIndex: setFocusActiveIndex,
            getChangeFocusLabels: getChangeFocusLabels,
            transientEdgeIndices: transientEdgeIndices,
            CSSVariables: CSSVariables,
        };
    },
    components: {
        SdsTransitionGroup: __vue_component__,
    },
});

export default script;
